import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';
import { useModal } from '../components/useModal';
import { formatCopenhagen } from '../../common/copenhagenTime';

/**
 * Seasonal court slot — one occurrence of a seasonal court.
 *
 * One action: cancelling it frees that single date, leaving the rest of the
 * season alone. The old page's dialog says "are you sure"; this one says which
 * of those two things happens, because "cancel" on a page about a recurring
 * booking reads either way.
 */

// Slot times arrive in UTC; shown in Copenhagen time with the zone (see copenhagenTime.js).
const fmtDate = value => formatCopenhagen(value, { fallback: '—' });

const SeasonalCourtSlotInternal = ({ api }) => {
  const { slotId } = useParams();
  const { ask, modalProps } = useModal();

  const [slot, setSlot] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.seasonalcourts.getSlot(slotId);
      if (!data || data.success === false || !data.Id) {
        setFailed(true);
        return;
      }
      setSlot(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, slotId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/seasonal-courts">← All seasonal courts</BackToList>
        <div className="next-note next-note--error">
          Could not load this slot.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!slot) return <div className="next-page next-muted">Loading…</div>;

  const cancel = () => ask({
    kind: 'danger',
    title: 'Cancel this occurrence?',
    context: `Cancel slot · ${fmtDate(slot.Start)}`,
    body: `Only ${fmtDate(slot.Start)} is freed. The rest of the season keeps running — cancelling the whole thing is done from the seasonal court purchase.`,
    warning: 'The booking is cancelled at the venue too.',
    confirmLabel: 'Cancel this occurrence',
    onConfirm: async () => {
      try {
        await api.seasonalcourts.cancelslot(slot.Id);
        setNote({ tone: 'info', text: 'Occurrence cancelled.' });
      } catch (error) {
        setNote({ tone: 'error', text: `Unable to cancel: ${error?.message || 'unknown error'}` });
        throw error;
      }
      await load();
    }
  });

  const facts = [
    ['Status', slot.Status
      ? <StatusPill status={slot.Status}>{slot.Status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['Start', fmtDate(slot.Start)],
    ['End', fmtDate(slot.End)],
    ['Seasonal court id', slot.SeasonalCourtId
      ? <Guid value={slot.SeasonalCourtId} to={`/next/seasonal-court/${slot.SeasonalCourtId}`} />
      : <span className="next-muted">—</span>],
    ['External activity id', slot.ExternalActivityId
      ? <Guid value={slot.ExternalActivityId} to={`/next/external-activity/${slot.ExternalActivityId}`} />
      : <span className="next-muted">—</span>],
    ['Created at', fmtDate(slot.CreatedAt)]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/seasonal-courts">← All seasonal courts</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{fmtDate(slot.Start)}</h1>
          {slot.Status && <StatusPill status={slot.Status}>{slot.Status}</StatusPill>}
        </div>
        <span className="next-detail__id">{slot.Id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Slot</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button
                type="button"
                className="next-actionlist__item next-actionlist__item--danger"
                onClick={cancel}
              >
                Cancel this occurrence
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                slot.SeasonalCourtId && {
                  label: 'Seasonal court',
                  hint: 'the whole season',
                  to: `/next/seasonal-court/${slot.SeasonalCourtId}`
                },
                slot.SeasonalCourtId && {
                  label: 'Purchases',
                  hint: 'of this court',
                  to: `/next/seasonal-court-purchases?seasonalCourtId=${slot.SeasonalCourtId}`
                },
                slot.ExternalActivityId && {
                  label: 'External activity',
                  to: `/next/external-activity/${slot.ExternalActivityId}`
                }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

SeasonalCourtSlotInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const SeasonalCourtSlot = withApi(SeasonalCourtSlotInternal);

SeasonalCourtSlot.requiresLogin = true;

export default SeasonalCourtSlot;
